import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { SearchBar } from "../components/SearchBar";
import { ArcGISSearchMap } from "../components/ArcGISSearchMap";
import { searchParcelsPublic, Parcel } from "../api/dataverse";
import { dataverseScopes } from "../auth/msalConfig";

export const ParcelSearchPage: React.FC = () => {
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();
  const { instance } = useMsal();

  // Load initial parcels
  useEffect(() => {
    handleSearch("", "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (query: string, municipio: string) => {
    setLoading(true);
    setSearched(true);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: dataverseScopes,
        account: instance.getActiveAccount() || undefined,
      });
      const results = await searchParcelsPublic(query, municipio, tokenResponse.accessToken);
      setParcels(results);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleParcelClick = (parcelId: string) => {
    navigate(`/parcelas/${parcelId}`);
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      "Disponible": "bg-green-100 text-green-700",
      "Arrendada": "bg-blue-100 text-blue-700",
      "Vendida": "bg-red-100 text-red-700",
      "En Proceso": "bg-amber-100 text-amber-700",
    };
    return colors[status] ?? "bg-gray-100 text-gray-700";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Parcelas</h1>
        <p className="text-gray-500 mt-1">Busque y explore parcelas de terrenos públicos de Puerto Rico</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <SearchBar
          onSearch={handleSearch}
          placeholder="Buscar por ID de parcela, finca ID..."
          loading={loading}
        />
      </div>

      {/* Map + Results Layout */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          <ArcGISSearchMap
            parcelIds={parcels.map(p => p.parcelId)}
            onParcelClick={handleParcelClick}
            height="600px"
          />
        </div>

        {/* Results List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {searched ? `${parcels.length} parcelas encontradas` : "Cargando..."}
              </span>
            </div>

            <div className="divide-y divide-gray-100 max-h-[540px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-400">Buscando...</div>
              ) : parcels.length === 0 ? (
                <div className="p-8 text-center text-gray-400">No se encontraron parcelas</div>
              ) : (
                parcels.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleParcelClick(p.parcelId)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{p.parcelId}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{p.municipio}</div>
                        {p.nombreAdquisicion && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">
                            {p.nombreAdquisicion}
                          </div>
                        )}
                      </div>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusBadge(p.availabilityStatus)}`}>
                        {p.availabilityStatus}
                      </span>
                    </div>
                    {p.cabida > 0 && (
                      <div className="text-xs text-gray-400 mt-1">{p.cabida} cuerdas</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
